import React, { useState, useEffect } from "react";
import PageBody from "Common/Custom/PageBody";
import {
    getAttendanceStats,
    getAllRostersForDashboard,
    getEmployeesByRoster
} from "adminApi/DutyRosterApi/AttendanceDashboardApi";
import Spinner from "Common/Globle/Spinner";
import AttendanceDashboardHeader from "./AttendanceDashboardList/Componates/AttendanceDashboardHeader";
import AttendanceDashboardForm from "./AttendanceDashboardList/Componates/AttendanceDashboardForm";
import AttendanceDashboardList from "./AttendanceDashboardList/Componates/AttendanceDashboardList";

/**
 * ============================================================================
 * ATTENDANCE DASHBOARD
 * ============================================================================
 * Provides a real-time overview of employee attendance across different rosters.
 * Filterable by Roster Type and Date.
 * ============================================================================
 */
const AttendanceDashboard = () => {
    const today = new Date().toISOString().split('T')[0];
    const [date, setDate] = useState(today);
    const [stats, setStats] = useState({ present: 0, absent: 0 });
    const [rosters, setRosters] = useState([]);
    const [activeRoster, setActiveRoster] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch Roster List once on mount
    useEffect(() => {
        const fetchRosterList = async () => {
            setLoading(true);
            try {
                const rosterRes = await getAllRostersForDashboard();
                if (rosterRes.success && rosterRes.data.length > 0) {
                    setRosters(rosterRes.data);
                    if (!activeRoster) {
                        setActiveRoster(rosterRes.data[0].roster_id);
                    }
                }
            } catch (err) {
                console.error("[AttendanceDashboard] Error fetching rosters:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchRosterList();
    }, []);

    // Sync Employees and Stats when filters change
    useEffect(() => {
        const fetchEmployeesAndStats = async () => {
            setLoading(true);
            try {
                const [res, statsRes] = await Promise.all([
                    activeRoster ? getEmployeesByRoster(date, activeRoster) : Promise.resolve({ success: false, data: [] }),
                    getAttendanceStats(date, activeRoster || null)
                ]);

                if (res.success) {
                    setEmployees(res.data);
                    const calculatedPresent = res.data.filter(e => e.attendance_status?.toLowerCase() === 'present').length;
                    const calculatedAbsent = res.data.filter(e => e.attendance_status?.toLowerCase() === 'absent').length;

                    setStats({
                        present: calculatedPresent,
                        absent: calculatedAbsent
                    });
                } else {
                    setEmployees([]);
                    if (statsRes.success) setStats(statsRes.data);
                }
            } catch (err) {
                console.error("[AttendanceDashboard] Error sync data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchEmployeesAndStats();
    }, [activeRoster, date]);

    return (
        <div className="attendance-dashboard-wrapper">
            <PageBody
                card={true}
                headerchildren={<AttendanceDashboardHeader stats={stats} />}
            >
                {loading && <Spinner isSpinner={true} />}

                <AttendanceDashboardForm
                    rosters={rosters}
                    activeRoster={activeRoster}
                    setActiveRoster={setActiveRoster}
                    date={date}
                    setDate={setDate}
                />

                <AttendanceDashboardList
                    employees={employees}
                    activeRoster={activeRoster}
                    rosters={rosters}
                />
            </PageBody>

            <style dangerouslySetInnerHTML={{
                __html: `
                .attendance-dashboard-wrapper .form-control:focus {
                    border-color: var(--primary-color) !important;
                    box-shadow: 0 0 0 0.25rem rgba(var(--primary-rgb), 0.1) !important;
                }
                .attendance-dashboard-wrapper .stats-mini-card {
                    min-width: 140px;
                    transition: transform 0.2s;
                }
                .attendance-dashboard-wrapper .stats-mini-card:hover {
                    transform: translateY(-2px);
                }
                .attendance-dashboard-wrapper .border-dashed {
                    border-style: dashed !important;
                }
            `}} />
        </div>
    );
};

export default AttendanceDashboard;
